import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import { MdClose, MdExpandMore } from '@teamworksdev/icons';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../../components/utils';
import type { HubCalendarEvent } from '../hooks/useCalendarEvents';
import {
  formatHubEventCreatedAt,
  formatHubEventDateTimeRange,
} from './hubEventFormatting';
import { getLocale } from '../../../../utils/i18n';

// Local display type — strips Zod passthrough index signatures from MetacalEvent
// so we can use dot notation under noPropertyAccessFromIndexSignature.
type HubAttendeeSummary = {
  first_name?: string | null;
  last_name?: string | null;
  profile_id?: string | null;
};

type HubEventDisplay = {
  title: string;
  start_date_time: string;
  end_date_time: string;
  timezone: string;
  is_all_day?: boolean | null;
  event_type?: { id?: string | null; name?: string | null } | null;
  description?: string | null;
  attendee_count?: number | null;
  attendee_summaries?: HubAttendeeSummary[] | null;
  creator?: { first_name?: string | null; last_name?: string | null } | null;
  created_at?: string | null;
};

type HubEventDetailModalProps = {
  event: HubCalendarEvent | null;
  onClose: () => void;
};

function formatCreatorName(event: HubEventDisplay): string | null {
  const creator = event.creator;
  if (!creator) {
    return null;
  }
  const parts = [creator.first_name, creator.last_name].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : null;
}

function formatAttendeeName(attendee: HubAttendeeSummary): string {
  const parts = [attendee.first_name, attendee.last_name].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : 'Unknown attendee';
}

export function HubEventDetailModal({
  event,
  onClose,
}: HubEventDetailModalProps) {
  const { t } = useTranslation();
  const open = event !== null;
  const displayEvent = event as HubEventDisplay | null;
  const creatorName = displayEvent ? formatCreatorName(displayEvent) : null;
  const createdAtLabel =
    displayEvent?.created_at != null
      ? formatHubEventCreatedAt(
          displayEvent.created_at,
          displayEvent.timezone,
          getLocale(),
        )
      : null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        className: 'w-[460px] max-w-[calc(100%-32px)]',
      }}
    >
      <DialogTitle className="flex items-center justify-between gap-2 px-6 py-6">
        <Typography
          sx={{ typography: 'h6' }}
          component="span"
          className="truncate"
        >
          {displayEvent?.title ?? ''}
        </Typography>
        <IconButton onClick={onClose} aria-label={t('close')} size="small">
          <MdClose size={24} aria-hidden="true" />
        </IconButton>
      </DialogTitle>
      {displayEvent && (
        <DialogContent className="flex flex-col gap-4 px-6 pb-6 pt-0">
          <DetailFieldRow
            label={t('hubEventDateTime')}
            value={formatHubEventDateTimeRange(
              displayEvent,
              getLocale(),
              t('hubAllDay'),
            )}
          />
          {displayEvent.event_type?.name && (
            <DetailFieldRow
              label={t('hubEventEventType')}
              value={displayEvent.event_type.name}
            />
          )}
          {displayEvent.description && (
            <DetailFieldRow
              label={t('hubEventDescription')}
              value={displayEvent.description}
            />
          )}
          {displayEvent.attendee_count != null && (
            <HubEventInvitedRow event={displayEvent} />
          )}
          {creatorName && (
            <DetailFieldRow
              label={t('hubEventCreator')}
              value={
                <div className="flex flex-col">
                  <Typography variant="body2Medium" color="text.primary">
                    {creatorName}
                  </Typography>
                  {createdAtLabel && (
                    <Typography variant="caption" color="text.secondary">
                      {createdAtLabel}
                    </Typography>
                  )}
                </div>
              }
            />
          )}
        </DialogContent>
      )}
    </Dialog>
  );
}

function HubEventInvitedRow({ event }: { event: HubEventDisplay }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const attendeeSummaries = event.attendee_summaries ?? [];
  const canExpand = attendeeSummaries.length > 0;
  const count = event.attendee_count ?? attendeeSummaries.length;

  return (
    <div className="flex gap-4 items-start w-full">
      <Typography
        variant="body2"
        color="text.secondary"
        className="w-[100px] shrink-0"
      >
        {t('hubEventInvited')}
      </Typography>
      <div className="flex flex-col flex-1 min-w-0 gap-1">
        {canExpand ? (
          <button
            type="button"
            className={cn(
              'flex items-center gap-1 w-fit text-left',
              'bg-transparent border-0 appearance-none p-0 m-0',
              'cursor-pointer hover:opacity-80',
            )}
            onClick={() => setExpanded((prev) => !prev)}
            aria-expanded={expanded}
          >
            <Typography variant="body2Medium" color="text.primary">
              {t('hubEventPeople', { count })}
            </Typography>
            <MdExpandMore
              size={16}
              className={cn('transition-transform', expanded && 'rotate-180')}
              aria-hidden="true"
            />
          </button>
        ) : (
          <Typography variant="body2Medium" color="text.primary">
            {t('hubEventPeople', { count })}
          </Typography>
        )}
        {expanded && (
          <div className="flex flex-col gap-1 pl-1">
            {attendeeSummaries.map((attendee, index) => (
              <Typography
                key={attendee.profile_id ?? `${index}`}
                variant="body2"
                color="text.primary"
              >
                {formatAttendeeName(attendee)}
              </Typography>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function DetailFieldRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex gap-4 items-start w-full">
      <Typography
        variant="body2"
        color="text.secondary"
        className="w-[100px] shrink-0"
      >
        {label}
      </Typography>
      {typeof value === 'string' ? (
        <Typography variant="body2Medium" className="min-w-0 flex-1">
          {value}
        </Typography>
      ) : (
        <div className="min-w-0 flex-1">{value}</div>
      )}
    </div>
  );
}
